import { Badge, Spinner, Table, type Column } from "../../components";
import { useAuthorityGaps } from "../../api/hooks";
import type { AuthorityGap } from "../../api/types";
import { t } from "../../i18n/t";

/**
 * P5-web §2f: a read-only list, no mutation available on this route — redrawn as PracticeGapsBudget.html's
 * "Clients missing authority" card: the client, its gaps as fail-toned pills, and the outstanding count on
 * the card header. The real `missing` values are the API's `"CDD_COMPLETE"` / `"ACTIVE"` (computed from
 * `status` and `cdd_status`, §C1a) — there is no in-app route that fixes either, so the design's sample
 * "Fix" links (Xero/LuciBook connections, consent) do not map to these and are not rendered.
 */
export function AuthorityGapsTab({ practiceId }: { practiceId: string }): JSX.Element {
  const gaps = useAuthorityGaps(practiceId);

  const columns: readonly Column<AuthorityGap>[] = [
    {
      key: "client",
      header: t("practice.gaps.col_client"),
      render: (row) => <span className="cellvendor__name">{row.client_id}</span>,
    },
    {
      key: "missing",
      header: t("practice.gaps.col_missing"),
      render: (row) => (
        <span className="gapspills">
          {row.missing.map((value) => (
            <Badge key={value} tone="fail">
              {value}
            </Badge>
          ))}
        </span>
      ),
    },
  ];

  if (gaps.isPending) return <Spinner />;

  return (
    <section className="card card--flush" aria-labelledby="gaps-title">
      <div className="card__header card__header--row">
        <h2 className="card__title" id="gaps-title">
          {t("practice.gaps.caption")}
        </h2>
        <span className="numfail mono">
          {t("practice.gaps.outstanding", { count: (gaps.data?.items ?? []).length })}
        </span>
      </div>
      <Table
        bare
        caption={t("practice.gaps.caption")}
        columns={columns}
        rows={gaps.data?.items ?? []}
        rowKey={(row) => row.client_id}
        emptyMessage={t("practice.gaps.empty")}
      />
    </section>
  );
}
